import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    Input,
    Button,
    Link
} from "@chakra-ui/react";
import {
    twoFactorContentProps,
    twoFactorTitleProps,
    twoFactorFieldProps,
    twoFactorCancelButtonProps,
    twoFactorConfirmButtonProps
} from "./twoFactorStyles";

/**
 * Renders the modal body: helper text, the 6-digit field, an inline status
 * message and a resend link.
 * @param {Object} props
 * @param {string} props.code - Current code value
 * @param {Function} props.setCode - Setter for the code value
 * @param {string|null} props.error - Inline status/error message
 * @param {Function} props.onResend - Resend handler
 * @returns {React.ReactElement}
 */
function TwoFactorModalBody({ code, setCode, error, onResend }) {
    return (
        <ModalBody>
            <Text color="rgba(255, 255, 255, 0.7)" mb={4} fontSize="0.9rem">
                Un code à 6 chiffres vient de vous être envoyé par email. Saisissez-le pour confirmer l'opération.
            </Text>
            <Input
                autoFocus
                placeholder="Code 2FA"
                value={code}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                {...twoFactorFieldProps}
            />
            {error && (
                <Text color="#ff9800" mt={3} fontSize="0.85rem">{error}</Text>
            )}
            <Link as="button" type="button" onClick={onResend}
                display="block" mt={3} color="#17dcb8" fontSize="0.85rem">
                Renvoyer le code
            </Link>
        </ModalBody>
    );
}

/**
 * Renders the modal action buttons (cancel and confirm).
 * @param {Object} props
 * @param {string} props.code - Current code value
 * @param {boolean} props.submitting - Whether a replay is in progress
 * @param {Function} props.onCancel - Cancel handler
 * @param {Function} props.onSubmit - Submit handler
 * @returns {React.ReactElement}
 */
function TwoFactorModalActions({ code, submitting, onCancel, onSubmit }) {
    return (
        <ModalFooter gap={2}>
            <Button onClick={onCancel} {...twoFactorCancelButtonProps}>Annuler</Button>
            <Button onClick={onSubmit} isDisabled={submitting || code.length < 6}
                {...twoFactorConfirmButtonProps}>
                {submitting ? "Vérification..." : "Confirmer"}
            </Button>
        </ModalFooter>
    );
}

/**
 * Modal asking the admin for the 6-digit code received by email during a 2FA
 * protected operation.
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {boolean} props.submitting - Whether a replay is in progress
 * @param {string|null} props.error - Inline status/error message
 * @param {Function} props.onSubmit - Called with the entered code
 * @param {Function} props.onResend - Triggers a fresh OTP email
 * @param {Function} props.onCancel - Aborts the 2FA flow
 * @returns {React.ReactElement}
 */
function TwoFactorModal({ open, submitting, error, onSubmit, onResend, onCancel }) {
    const [code, setCode] = useState("");
    useEffect(() => { if (!open) setCode(""); }, [open]);

    return (
        <Modal isOpen={open} onClose={onCancel} size="xs" isCentered>
            <ModalOverlay />
            <ModalContent {...twoFactorContentProps}>
                <ModalHeader {...twoFactorTitleProps}>Vérification 2FA</ModalHeader>
                <TwoFactorModalBody code={code} setCode={setCode} error={error} onResend={onResend} />
                <TwoFactorModalActions code={code} submitting={submitting}
                    onCancel={onCancel} onSubmit={() => onSubmit(code)} />
            </ModalContent>
        </Modal>
    );
}

export default TwoFactorModal;
